import { Link, Navigate, useParams } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, BookOpen, GraduationCap, Layers, Server, Database, MonitorSmartphone, Usb, Cctv } from 'lucide-react';
import { Button, CheckList, Container, GradientText, IconTile, Reveal, SectionHeading } from '@/components/ui';
import { CtaBand, PageHero, TrainingBand } from '@/components/sections';
import { ModuleVisual, DashboardMockup } from '@/components/mockups';
import { ScreenshotSlider } from '@/components/ScreenshotSlider';
import { usePageTitle } from '@/components/layout/SiteLayout';
import { MODULE_DOCS, PLATFORM_MODULES, MODULE_SLUG_ALIASES, findModule } from '@/content/platform';
import { courseTitle } from '@/content/training';
import { HELP_URL, INK_U_URL, helpLink, orderLink } from '@/lib/site';

export function PlatformOverviewPage() {
  usePageTitle('Platform');
  const layers = [
    { icon: Server, title: 'Drivers', body: 'Native protocol drivers poll devices on the appliance.' },
    { icon: Layers, title: 'Tag engine & API', body: 'Tags, expressions, alarms, scripts and workflows.' },
    { icon: Database, title: 'Historian', body: 'Time-series and SQL history, OEE, SPC and reports.' },
    { icon: MonitorSmartphone, title: 'Web interface', body: 'Dashboards and pages in any browser on the network.' },
  ];
  return (
    <>
      <PageHero
        crumbs={[{ label: 'Platform' }]}
        eyebrow="The Scada.Ink platform"
        title={
          <>
            SCADA, historian, analytics and MES — <GradientText>on one appliance</GradientText>
          </>
        }
        intro="Every capability runs on the appliance in your panel. Your license decides what is unlocked, not what you install, so growing from monitoring to full MES is a new license key, not a new project."
        actions={
          <>
            <Button to={orderLink('quote')} size="lg" arrow>
              Get a quote
            </Button>
            <Button href="/overview.pdf" size="lg" variant="secondary" download>
              Download overview
            </Button>
          </>
        }
        visual={<DashboardMockup />}
      />

      <section className="py-20 lg:py-24">
        <Container>
          <SectionHeading eyebrow="Capabilities" title="Explore the platform" intro="Eight areas, one data model. Everything that reads a tag can read every tag." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {PLATFORM_MODULES.map((m, i) => (
              <Reveal key={m.slug} delay={i * 50}>
                <Link to={`/platform/${m.slug}`} className="group glass-card h-full p-6 flex flex-col hover:shadow-lg hover:-translate-y-0.5 transition-all">
                  <IconTile icon={m.icon} color={m.color} />
                  <h3 className="mt-4 font-semibold group-hover:text-brand-primary transition-colors">{m.name}</h3>
                  <p className="mt-1.5 text-sm text-text-secondary leading-relaxed flex-1">{m.summary}</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-brand-primary">
                    Learn more <ArrowRight size={12} className="group-hover:translate-x-0.5 transition-transform" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      <section className="py-20 bg-white border-y border-glass-border">
        <Container>
          <SectionHeading eyebrow="Architecture" title="Four layers, one box" align="center" intro="No servers to provision, no client licenses, no internet dependency." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {layers.map((l, i) => (
              <Reveal key={l.title} delay={i * 80}>
                <div className="relative glass-panel p-6 h-full">
                  <div className="text-xs font-bold text-text-muted">Layer {i + 1}</div>
                  <l.icon size={24} className="mt-3 text-brand-primary" />
                  <h3 className="mt-3 font-semibold">{l.title}</h3>
                  <p className="mt-1.5 text-sm text-text-secondary">{l.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      <TrainingBand />
      <CtaBand />
    </>
  );
}

export function ModulePage() {
  const { slug } = useParams();
  const module = findModule(slug);
  usePageTitle(module?.name ?? 'Platform');
  if (!module) {
    const moved = slug ? MODULE_SLUG_ALIASES[slug] : undefined;
    return <Navigate to={moved ? `/platform/${moved}` : '/platform'} replace />;
  }

  const others = PLATFORM_MODULES.filter((m) => m.slug !== module.slug);

  return (
    <>
      <PageHero
        crumbs={[{ label: 'Platform', to: '/platform' }, { label: module.name }]}
        eyebrow={module.name}
        title={module.headline}
        intro={module.intro}
        actions={
          <>
            <Button to={orderLink('quote')} size="lg" arrow>
              Get a quote
            </Button>
            <Button to={orderLink('demo')} size="lg" variant="secondary">
              See it in a demo
            </Button>
          </>
        }
        visual={<ModuleVisual visual={module.visual} />}
      />

      {module.gallery && (
        <section className="py-20 lg:py-24 bg-white border-y border-glass-border">
          <Container>
            <SectionHeading eyebrow={module.gallery.eyebrow} title={module.gallery.title} intro={module.gallery.intro} align="center" />
            <div className="max-w-5xl mx-auto">
              <ScreenshotSlider slides={module.gallery.slides} color={module.color} />
            </div>
          </Container>
        </section>
      )}

      {module.compatibility && (
        <section className="py-20 lg:py-24 bg-white border-y border-glass-border">
          <Container>
            <div className="grid lg:grid-cols-[1fr_1.1fr] gap-12 items-center">
              <div>
                <SectionHeading
                  eyebrow={module.compatibility.eyebrow}
                  title={module.compatibility.title}
                  intro={module.compatibility.intro}
                />
                <div className="-mt-4">
                  <CheckList items={module.compatibility.points} color={module.color} />
                </div>
              </div>
              <div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {module.compatibility.devices.map((d, i) => (
                    <Reveal key={d.name} delay={i * 60}>
                      <div className="glass-card h-full p-5 flex flex-col items-center justify-center text-center gap-3">
                        <div className="h-12 w-full flex items-center justify-center">
                          {d.logo ? (
                            <img src={d.logo} alt={`${d.name} logo`} className="max-h-9 max-w-[85%] object-contain" loading="lazy" />
                          ) : (
                            <IconTile icon={d.icon === 'usb' ? Usb : Cctv} color={module.color} size="lg" />
                          )}
                        </div>
                        <div>
                          <div className="text-sm font-semibold">{d.name}</div>
                          <div className="text-xs text-text-muted mt-0.5">{d.detail}</div>
                        </div>
                      </div>
                    </Reveal>
                  ))}
                  {/* The tile that makes the point: the same tools behind all of them. */}
                  <Reveal delay={module.compatibility.devices.length * 60}>
                    <div
                      className="h-full rounded-[var(--radius-glass)] p-5 flex flex-col items-center justify-center text-center gap-2 text-white"
                      style={{ background: `linear-gradient(135deg, ${module.color}, #7c3aed)` }}
                    >
                      <div className="text-sm font-semibold">Same tools on every one</div>
                      <div className="text-xs text-white/85">One setup, one set of tags</div>
                    </div>
                  </Reveal>
                </div>
                {module.compatibility.footnote && (
                  <p className="mt-4 text-[11px] text-text-muted leading-relaxed">{module.compatibility.footnote}</p>
                )}
              </div>
            </div>
          </Container>
        </section>
      )}

      <section className="py-20 lg:py-24">
        <Container>
          <SectionHeading eyebrow="Capabilities" title={`What ${module.name} does`} />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {module.capabilities.map((c, i) => (
              <Reveal key={c.title} delay={i * 60}>
                <div className="glass-card h-full p-6">
                  <div className="w-9 h-1 rounded-full mb-5" style={{ backgroundColor: module.color }} />
                  <h3 className="font-semibold">{c.title}</h3>
                  <p className="mt-2 text-sm text-text-secondary leading-relaxed">{c.body}</p>
                </div>
              </Reveal>
            ))}
          </div>


          {MODULE_DOCS[module.slug] && (
            <div className="mt-8 flex flex-col md:flex-row md:items-center gap-3 md:gap-5 rounded-[var(--radius-glass-sm)] border border-glass-border bg-white px-5 py-4">
              <a href={HELP_URL} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm font-semibold shrink-0 hover:text-brand-primary">
                <BookOpen size={16} className="text-brand-primary" /> Documentation
              </a>
              <div className="flex flex-wrap gap-x-5 gap-y-2">
                {MODULE_DOCS[module.slug].map((d) => (
                  <a
                    key={d.path}
                    href={helpLink(d.path)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-text-secondary hover:text-brand-primary transition-colors"
                  >
                    {d.label} <ArrowUpRight size={12} />
                  </a>
                ))}
              </div>
            </div>
          )}

          {module.chipGroups && (
            <div className="mt-14 glass-panel p-6 lg:p-8">
              <h3 className="text-lg font-semibold mb-6">Supported protocols & devices</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {module.chipGroups.map((g) => (
                  <div key={g.title}>
                    <div className="text-xs font-semibold uppercase tracking-[0.14em] text-text-muted mb-2.5">{g.title}</div>
                    <div className="flex flex-wrap gap-1.5">
                      {g.chips.map((chip) => (
                        <span key={chip} className="px-2.5 py-1 rounded-md bg-ink-muted border border-glass-border text-xs font-medium text-text-primary">
                          {chip}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Container>
      </section>

      {/* Learn it on Ink U */}
      {module.featureBand && (
        <section className="py-20 lg:py-24 bg-white border-y border-glass-border">
          <Container>
            <SectionHeading eyebrow={module.featureBand.eyebrow} title={module.featureBand.title} intro={module.featureBand.intro} />
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {module.featureBand.items.map((item, i) => (
                <Reveal key={item.title} delay={i * 50}>
                  <div className="h-full rounded-[var(--radius-glass-sm)] border border-glass-border bg-ink-base p-5">
                    <div className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: module.color }} />
                      <h3 className="text-sm font-semibold">{item.title}</h3>
                    </div>
                    <p className="mt-1.5 text-sm text-text-secondary leading-relaxed">{item.body}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </Container>
        </section>
      )}

      {/* Normally sits right under the capabilities, which supply the space above it. */}
      <section className={module.featureBand ? 'py-20' : 'pb-20'}>
        <Container>
          <div className="rounded-[var(--radius-glass-lg)] bg-gradient-to-br from-[#eef2ff] to-[#f5f3ff] border border-glass-border p-8 lg:p-10 grid lg:grid-cols-[1fr_auto] gap-8 items-center">
            <div className="flex items-start gap-5">
              <span className="w-12 h-12 rounded-xl bg-brand-secondary text-white flex items-center justify-center shrink-0">
                <GraduationCap size={24} />
              </span>
              <div>
                <h3 className="text-xl font-bold">Learn {module.name} on Ink U — free</h3>
                <p className="mt-1.5 text-sm text-text-secondary">Video lessons, notes and quizzes that count toward Scada.Ink certification:</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {module.courses.map((n) => (
                    <span key={n} className="px-2.5 py-1 rounded-full bg-white border border-glass-border text-xs font-medium">
                      Course {n} · {courseTitle(n)}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button href={INK_U_URL}>Start the course</Button>
              <Button to="/training/certification" variant="secondary">
                Certification
              </Button>
            </div>
          </div>
        </Container>
      </section>

      <section className="py-16 bg-white border-t border-glass-border">
        <Container>
          <h2 className="text-xl font-bold mb-6">Explore more of the platform</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {others.map((m) => (
              <Link key={m.slug} to={`/platform/${m.slug}`} className="group flex items-center gap-3 glass-card p-3.5 hover:shadow-md transition-shadow">
                <IconTile icon={m.icon} color={m.color} size="sm" />
                <span className="text-sm font-semibold group-hover:text-brand-primary transition-colors">{m.name}</span>
              </Link>
            ))}
            <Link to="/hardware" className="group flex items-center gap-3 glass-card p-3.5 hover:shadow-md transition-shadow">
              <IconTile icon={Server} color="#64748b" size="sm" />
              <span className="text-sm font-semibold group-hover:text-brand-primary transition-colors">Hardware</span>
            </Link>
          </div>
        </Container>
      </section>

      <CtaBand />
    </>
  );
}
